import React from 'react'
import cn from 'classnames'
import { ArrowUpCircle, Bitcoin, Bot, Medal, Tag } from 'lucide-react'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import DailyMoneyValue from 'components/shared/DailyMoneyValue'
import SatsIcon from 'components/shared/SatsIcon'
import { bsvToSats, dailyMoneyText } from 'components/shared/dailyMoney'
import useDailyMoney from 'components/shared/useDailyMoney'
import InitialsAvatar from 'components/users/InitialsAvatar'
import { avatarInitials } from 'components/users/avatarInitials'
import { REACTION_OPTIONS } from 'components/votes/reactions'
import { SATOSHIS } from 'lib/bsv-util'
import { barWidths, shareLabel } from './splitBar'
import styles from './tdp-cards.module.scss'

type TdpCardVariant = 'modal' | 'embed'

export type TdpReactionUpvalue = {
    emoji: string
    total_bsv: number
}

interface TdpValueCardProps {
    aiFeesBsv: number
    contextLabel?: React.ReactNode
    heading?: React.ReactNode
    headingSubtext?: React.ReactNode
    ordinalSalesBsv: number
    rangeAddedBsv: number
    upvaluesBsv: number
    variant: TdpCardVariant
}

export type TdpShareRewardsSharer = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    total_sats: number
    share_count: number
}

interface TdpShareRewardsCardProps {
    emptyLabel?: React.ReactNode
    heading?: React.ReactNode
    headingSubtext?: React.ReactNode
    shareCount: number
    sharerCount: number
    sharers: TdpShareRewardsSharer[]
    totalSats: number
    variant: TdpCardVariant
}

interface TdpSentimentCardProps {
    emptyLabel?: React.ReactNode
    heading?: React.ReactNode
    headingSubtext?: React.ReactNode
    reactions: TdpReactionUpvalue[]
    variant: TdpCardVariant
}

const REACTION_LABELS = new Map(REACTION_OPTIONS.map(option => [option.emoji, option.label]))

export function formatBsv(value: number) {
    if (!Number.isFinite(value)) return '0'
    const decimals = value >= 100 ? 0 : value >= 1 ? 2 : 4
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: decimals }).format(value)
}

export function TdpValueCard({
    aiFeesBsv,
    contextLabel,
    heading,
    headingSubtext,
    ordinalSalesBsv,
    rangeAddedBsv,
    upvaluesBsv,
    variant,
}: TdpValueCardProps) {
    const { useUsd, formatSatsAmount } = useBsvDisplay()
    // The shareable embed follows the daily money rule (what you see is what
    // the share card says); the modal keeps the viewer's unit preference.
    const embedMoney = variant === 'embed'
    const dailyMoney = useDailyMoney({ enabled: embedMoney })
    const formatBsvDisplay = React.useCallback(
        (bsv: number, { includeUnit = false }: { includeUnit?: boolean } = {}) =>
            useUsd
                ? formatSatsAmount(bsv * SATOSHIS, { includeUnit, compactSats: false })
                : `${formatBsv(bsv)}${includeUnit ? ' BSV' : ''}`,
        [formatSatsAmount, useUsd],
    )
    const totalMoney = embedMoney ? dailyMoney(bsvToSats(rangeAddedBsv)) : null
    const formattedRangeAddedBsv = totalMoney ? totalMoney.text : formatBsvDisplay(rangeAddedBsv)
    const valueRowStyle = React.useMemo(
        () =>
            ({
                '--tdp-card-fit-divisor': String(
                    1 + Math.max(formattedRangeAddedBsv.length, 1) * 0.6,
                ),
            } as React.CSSProperties),
        [formattedRangeAddedBsv],
    )
    const iconSize = variant === 'embed' ? 14 : 16
    // The TDP sources, in the order the split bar stacks them.
    const sources = [
        {
            key: 'upvalues',
            label: 'Upvalues',
            title: 'Upvalues',
            bsv: upvaluesBsv,
            Icon: ArrowUpCircle,
            iconClassName: styles.upvalueIcon,
            barClassName: styles.barUpvalues,
            sectorClassName: styles.sectorUpvalues,
        },
        {
            key: 'ai',
            label: 'AI',
            title: 'AI fees',
            bsv: aiFeesBsv,
            Icon: Bot,
            iconClassName: styles.aiIcon,
            barClassName: styles.barAi,
            sectorClassName: styles.sectorAi,
        },
        {
            key: 'sales',
            label: 'Sales',
            title: 'Ordinal sales',
            bsv: ordinalSalesBsv,
            Icon: Tag,
            iconClassName: styles.salesIcon,
            barClassName: styles.barSales,
            sectorClassName: styles.sectorSales,
        },
    ]
    const shareOf = (bsv: number) =>
        rangeAddedBsv > 0 ? Math.min(100, Math.max(0, (bsv / rangeAddedBsv) * 100)) : 0
    const segmentWidths = barWidths(
        sources.map(source => shareOf(source.bsv)),
        sources.map(source => source.bsv > 0),
    )
    const sourceTitle = (title: string, bsv: number) =>
        `${title}: ${
            embedMoney
                ? dailyMoneyText(dailyMoney(bsvToSats(bsv)))
                : formatBsvDisplay(bsv, { includeUnit: true })
        }`

    return (
        <div
            className={cn(
                styles.valueCard,
                variant === 'modal' ? styles.modalValueCard : styles.embedValueCard,
            )}
        >
            {heading && <CardHeader label={heading} subtext={headingSubtext} />}
            {contextLabel && <div className={styles.rangeContextLabel}>{contextLabel}</div>}
            <div
                className={cn(styles.tdpValueRow, variant === 'embed' && styles.embedValueRow)}
                style={valueRowStyle}
            >
                {totalMoney
                    ? totalMoney.unit === 'sats' && (
                          <SatsIcon inline contextStyles={styles.satsIcon} />
                      )
                    : !useUsd && <Bitcoin className={styles.btcIcon} />}
                <div className={cn(styles.tdpValue, totalMoney && styles.embedMoneyValue)}>
                    {formattedRangeAddedBsv}
                </div>
            </div>
            {embedMoney ? (
                // The shareable embed gives each sector its own line — icon
                // chip, name, share of the day and the money — with the
                // three-color split bar beneath the rows as their legend.
                <>
                    <div className={styles.sectorList}>
                        {sources.map(({ key, title, bsv, Icon, sectorClassName }) => (
                            <div
                                key={key}
                                className={cn(styles.sectorRow, sectorClassName)}
                                title={sourceTitle(title, bsv)}
                                data-testid={`tdp-sector-${key}`}
                            >
                                <span className={styles.sectorIcon}>
                                    <Icon size={15} aria-hidden />
                                </span>
                                <span className={styles.sectorLabel}>{title}</span>
                                <span className={styles.sectorShare}>
                                    {shareLabel(shareOf(bsv), bsv > 0)}
                                </span>
                                <span className={styles.sectorValue}>
                                    <DailyMoneyValue money={dailyMoney(bsvToSats(bsv))} />
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className={cn(styles.visualBar, styles.sectorBar)}>
                        {sources.map(({ key, barClassName }, i) => (
                            <div
                                key={key}
                                className={barClassName}
                                style={{ width: `${segmentWidths[i]}%` }}
                            />
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.breakdown}>
                        {sources.map(({ key, label, title, bsv, Icon, iconClassName }) => (
                            <div
                                key={key}
                                className={styles.breakdownItem}
                                title={`${title}: ${formatBsvDisplay(bsv, { includeUnit: true })}`}
                            >
                                <div className={styles.breakdownLabel}>
                                    <Icon className={iconClassName} size={iconSize} aria-hidden />
                                    <span className={styles.breakdownLabelText}>{label}</span>
                                </div>
                                <div className={styles.breakdownValue}>{formatBsvDisplay(bsv)}</div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.visualBar}>
                        {sources.map(({ key, barClassName }, i) => (
                            <div
                                key={key}
                                className={barClassName}
                                style={{ width: `${segmentWidths[i]}%` }}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

export function TdpShareRewardsCard({
    emptyLabel = 'No share rewards sent in this range.',
    heading = 'Share Rewards',
    headingSubtext,
    shareCount,
    sharerCount,
    sharers,
    totalSats,
    variant,
}: TdpShareRewardsCardProps) {
    const { useUsd, formatSatsAmount } = useBsvDisplay()
    const embedMoney = variant === 'embed'
    const dailyMoney = useDailyMoney({ enabled: embedMoney })
    const totalMoney = embedMoney ? dailyMoney(totalSats) : null
    const formattedTotal = totalMoney
        ? totalMoney.text
        : formatSatsAmount(totalSats, { compactSats: false })
    const valueRowStyle = React.useMemo(
        () =>
            ({
                '--tdp-card-fit-divisor': String(1 + Math.max(formattedTotal.length, 1) * 0.6),
            } as React.CSSProperties),
        [formattedTotal],
    )
    const subtext = (
        <>
            {headingSubtext}
            {headingSubtext ? ' · ' : ''}
            {shareCount.toLocaleString()} {shareCount === 1 ? 'view' : 'views'}
            {' · '}
            {sharerCount.toLocaleString()} {sharerCount === 1 ? 'sharer' : 'sharers'}
        </>
    )

    return (
        <div
            className={cn(
                styles.valueCard,
                variant === 'modal' ? styles.modalValueCard : styles.embedValueCard,
            )}
        >
            {heading && <CardHeader label={heading} subtext={subtext} />}
            <div
                className={cn(
                    styles.tdpValueRow,
                    variant === 'embed' && styles.embedValueRow,
                    styles.shareValueRow,
                )}
                style={valueRowStyle}
            >
                {totalMoney
                    ? totalMoney.unit === 'sats' && (
                          <SatsIcon inline contextStyles={styles.satsIcon} />
                      )
                    : !useUsd && <Bitcoin className={styles.btcIcon} />}
                <div className={cn(styles.tdpValue, totalMoney && styles.embedMoneyValue)}>
                    {formattedTotal}
                </div>
            </div>

            {sharers.length > 0 ? (
                <div className={styles.sharerList}>
                    {sharers.map((sharer, idx) => (
                        <div key={sharer.user.id} className={styles.sharerRow}>
                            <div className={styles.sharerRank}>
                                {idx === 0 ? <Medal size={12} /> : idx + 1}
                            </div>
                            {sharer.user.avatar_url ? (
                                <img
                                    className={styles.sharerAvatar}
                                    src={sharer.user.avatar_url}
                                    alt={sharer.user.name}
                                />
                            ) : avatarInitials(sharer.user.name) ? (
                                <InitialsAvatar
                                    className={styles.sharerAvatar}
                                    user={sharer.user}
                                />
                            ) : (
                                <div className={styles.sharerAvatar} />
                            )}
                            <div className={styles.sharerName}>{sharer.user.name}</div>
                            <div
                                className={cn(styles.sharerValue, embedMoney && styles.moneyValue)}
                            >
                                {embedMoney ? (
                                    <DailyMoneyValue money={dailyMoney(sharer.total_sats)} />
                                ) : (
                                    formatSatsAmount(sharer.total_sats, { compactSats: false })
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.sharersEmpty}>{emptyLabel}</div>
            )}
        </div>
    )
}

export function TdpSentimentCard({
    emptyLabel = 'No reaction upvalues in this range yet.',
    heading = 'Treechat Sentiment',
    headingSubtext,
    reactions,
    variant,
}: TdpSentimentCardProps) {
    const { useUsd, formatSatsAmount } = useBsvDisplay()
    const embedMoney = variant === 'embed'
    const dailyMoney = useDailyMoney({ enabled: embedMoney })
    const formatBsvDisplay = React.useCallback(
        (bsv: number, { includeUnit = false }: { includeUnit?: boolean } = {}) =>
            useUsd
                ? formatSatsAmount(bsv * SATOSHIS, { includeUnit, compactSats: false })
                : `${formatBsv(bsv)}${includeUnit ? ' BSV' : ''}`,
        [formatSatsAmount, useUsd],
    )
    const topReactionMaxBsv = reactions[0]?.total_bsv ?? 0

    return (
        <div
            className={cn(
                styles.sentimentCard,
                variant === 'modal' ? styles.modalSentimentCard : styles.embedSentimentCard,
            )}
        >
            <div className={styles.sentimentHeader}>
                <div className={styles.sentimentSubtitle}>
                    <span className={styles.headingLabel}>{heading}</span>
                    {headingSubtext && (
                        <span className={styles.headingSubtext}>{headingSubtext}</span>
                    )}
                </div>
            </div>

            {reactions.length > 0 ? (
                <div
                    className={cn(
                        styles.sentimentChart,
                        variant === 'embed' && styles.embedSentimentChart,
                    )}
                >
                    {reactions.map(reaction => (
                        <div
                            key={reaction.emoji}
                            className={styles.sentimentBarGroup}
                            title={`${REACTION_LABELS.get(reaction.emoji) || 'Reaction'}: ${
                                embedMoney
                                    ? dailyMoneyText(dailyMoney(bsvToSats(reaction.total_bsv)))
                                    : formatBsvDisplay(reaction.total_bsv, { includeUnit: true })
                            }`}
                        >
                            <div className={styles.sentimentEmoji}>{reaction.emoji}</div>
                            <div className={styles.sentimentBarTrack}>
                                <div
                                    className={styles.sentimentBarFill}
                                    style={{
                                        height: `${
                                            topReactionMaxBsv > 0
                                                ? (reaction.total_bsv / topReactionMaxBsv) * 100
                                                : 0
                                        }%`,
                                    }}
                                />
                            </div>
                            <div
                                className={cn(
                                    styles.sentimentAmount,
                                    embedMoney && styles.moneyValue,
                                )}
                            >
                                {embedMoney ? (
                                    <DailyMoneyValue
                                        money={dailyMoney(bsvToSats(reaction.total_bsv))}
                                    />
                                ) : (
                                    formatBsvDisplay(reaction.total_bsv)
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.sentimentEmpty}>{emptyLabel}</div>
            )}
        </div>
    )
}

function CardHeader({ label, subtext }: { label: React.ReactNode; subtext?: React.ReactNode }) {
    return (
        <div className={styles.cardHeader}>
            <div className={styles.cardHeaderLabel}>
                <span className={styles.headingLabel}>{label}</span>
                {subtext && <span className={styles.headingSubtext}>{subtext}</span>}
            </div>
        </div>
    )
}
